<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, url } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('assistantDocuments/getUIFlags'),
  agents: useMapGetter('assistantAgents/getRecords'),
};

const initialState = {
  assistantId: props.assistantId,
  name: '',
  documentType: 'url',
  url: '',
  content: '',
  file: null,
};

const state = reactive({ ...initialState });

const validationRules = {
  assistantId: { required },
  url: {
    required: requiredIf(() => state.documentType === 'url'),
    url,
  },
  content: {
    required: requiredIf(() => state.documentType === 'content'),
  },
  file: {
    required: requiredIf(() => state.documentType === 'file'),
  },
};

const documentTypeOptions = computed(() => [
  { value: 'url', label: t('ASSISTANT.DOCUMENTS.FORM.TYPE.URL') },
  { value: 'content', label: t('ASSISTANT.DOCUMENTS.FORM.TYPE.CONTENT') },
  { value: 'file', label: t('ASSISTANT.DOCUMENTS.FORM.TYPE.FILE') },
]);

const agentList = computed(() =>
  formState.agents.value.map(agent => ({
    value: agent.id,
    label: agent.name,
  }))
);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`ASSISTANT.DOCUMENTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  assistantId: getErrorMessage('assistantId', 'AGENT'),
  url: getErrorMessage('url', 'URL'),
  content: getErrorMessage('content', 'CONTENT'),
  file: getErrorMessage('file', 'FILE'),
}));

const handleCancel = () => emit('cancel');

const handleFileChange = event => {
  state.file = event.target.files?.[0] || null;
};

const prepareDocumentDetails = () => {
  if (state.documentType === 'file') {
    return {
      assistant_id: state.assistantId,
      name: state.name,
      file: state.file,
    };
  }

  const details = {
    assistant_id: state.assistantId,
    name: state.name || state.url,
  };

  if (state.documentType === 'url') {
    details.external_link = state.url;
  } else {
    details.content = state.content;
  }

  return details;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareDocumentDetails());
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label for="agent" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.DOCUMENTS.FORM.AGENT.LABEL') }}
      </label>
      <ComboBox
        id="agent"
        v-model="state.assistantId"
        :options="agentList"
        :has-error="!!formErrors.assistantId"
        :placeholder="t('ASSISTANT.DOCUMENTS.FORM.AGENT.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2"
        :message="formErrors.assistantId"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="documentType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('ASSISTANT.DOCUMENTS.FORM.TYPE.LABEL') }}
      </label>
      <ComboBox
        id="documentType"
        v-model="state.documentType"
        :options="documentTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-if="state.documentType === 'url'"
      v-model="state.url"
      :label="t('ASSISTANT.DOCUMENTS.FORM.URL.LABEL')"
      :placeholder="t('ASSISTANT.DOCUMENTS.FORM.URL.PLACEHOLDER')"
      :message="formErrors.url"
      :message-type="formErrors.url ? 'error' : 'info'"
    />

    <TextArea
      v-if="state.documentType === 'content'"
      v-model="state.content"
      :label="t('ASSISTANT.DOCUMENTS.FORM.CONTENT.LABEL')"
      :placeholder="t('ASSISTANT.DOCUMENTS.FORM.CONTENT.PLACEHOLDER')"
      :max-length="100000"
      auto-height
      :message="formErrors.content"
      :message-type="formErrors.content ? 'error' : 'info'"
    />

    <div v-if="state.documentType === 'file'" class="flex flex-col gap-1">
      <label for="file" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.DOCUMENTS.FORM.FILE.LABEL') }}
      </label>
      <input
        id="file"
        type="file"
        accept=".pdf,.docx,.txt,.md"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
        @change="handleFileChange"
      />
      <span
        class="text-xs"
        :class="formErrors.file ? 'text-n-ruby-11' : 'text-n-slate-11'"
      >
        {{ formErrors.file || t('ASSISTANT.DOCUMENTS.FORM.FILE.HELP_TEXT') }}
      </span>
    </div>

    <Input
      v-model="state.name"
      :label="t('ASSISTANT.DOCUMENTS.FORM.NAME.LABEL')"
      :placeholder="t('ASSISTANT.DOCUMENTS.FORM.NAME.PLACEHOLDER')"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('ASSISTANT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('ASSISTANT.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
